import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  OnDestroy
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NotificationService } from '../notification/services/notification.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Observable,
  debounceTime,
  filter,
  switchMap,
  firstValueFrom,
  Subject,
  takeUntil,
  distinctUntilChanged,
} from 'rxjs';

import { IAddressDetails, IAutocompleteResponse, ICoordate, IGeocodeResult, IGeocodeResultV2, IPlacePrediction, IUserProfileResponse } from '@core/interface';
import { CommonModule } from '@angular/common';

import { ButtonLibComponent } from '@components/atoms';
import { getCountryISOAlfa2, scrollToTop } from '@core/utils';
import { GoogleDynamicMapComponent } from '@core/maps';
import { GoogleMapsApiService, UserService as CoreUserService } from '@core/services';
import { StorageKeys, COUNTRY_CODE } from '@core/enums';
import { SharedModuleLib } from '../../shared.module';

@Component({
  selector: 'agenda-customer-ui-form-address',
  templateUrl: './form-address.component.html',
  styleUrls: ['./form-address.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    ButtonLibComponent,
    SharedModuleLib,
    GoogleDynamicMapComponent
  ],
})
export class FormAddressComponent implements OnInit, OnChanges, OnDestroy {
  @Output() userData = new EventEmitter<IUserProfileResponse>();
  @Output() restartPudComponent = new EventEmitter();
  @Output() changeAddressOnMaps = new EventEmitter();
  @Input() origin: 'schedule' | 'shell' = 'schedule';
  @Input() enableAddressForm = false;

  updateAddress!: FormGroup;
  userProfile!: IUserProfileResponse;
  suggestObservable$!: Observable<IAutocompleteResponse>;
  showFinder = false;
  showIconsForm = false;
  showForm = true;
  loading = true;
  enableUpdateUserButton = false;
  isWebAccess = false;
  showMap = false;
  dealer = JSON.parse(localStorage.getItem('@Ford-dealerSelected') as string) || null;
  originAddress = '';
  originalAddress3 = '';
  originalCoordinates: ICoordate | undefined;
  enableSearch = true;
  noStreetNumber = false;

  // ggl
  latLngLiteral?: { lat: number; lng: number };
  addressFound: IGeocodeResult | null = null;
  // Endereço estruturado retornado pelo placeDetailsV2 (backend), usado preferencialmente ao montar o payload do usuário
  addressDetailsV2: IAddressDetails | null = null;
  isValidFields = false;

  private destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private translate: TranslateService,
    private fb: FormBuilder,
    private googleMapsApiService: GoogleMapsApiService,
    private coreUserService: CoreUserService
  ) {
    this.updateAddress = this.fb.group({
      control: [null, [Validators.required]],
      address3: [null],
    });
  }
  async ngOnInit(): Promise<void> {
    await this.#initForm();
    this.#handleLoading(false);
    this.#searchEngine();
    this.#setupFormChangeTracking();
    if (this.origin !== 'shell') scrollToTop();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(): void {
    if (this.checkIfOriginIsFromShell()) {
      this.enableForm();
    }
  }

  enableForm() {
    if (this.enableAddressForm) {
      this.updateAddress.enable();
      return;
    }
    this.updateAddress.disable();
    this.getOriginalInfoAfterCancel();
  }

  getOriginalInfoAfterCancel() {
    this.updateAddress.get('control')?.setValue(this.originAddress);
    this.updateAddress.get('address3')?.setValue(this.originalAddress3);

    this.emitUserData(this.checkIfOriginIsFromShell());
  }

  async emitUserData(submit = false) {
    if (!submit) {
      return;
    }
    const isUserPayloadCreated = await this.#createUserPayload();
    if (isUserPayloadCreated) {
      this.userData.emit(this.userProfile);
    }
  }

  checkIfOriginIsFromShell() {
    return this.origin === 'shell';
  }

  async setComplement(): Promise<void> {
    this.emitUserData(this.checkIfOriginIsFromShell());

    if (this.origin === 'shell' && this.enableAddressForm) {
      await this.#handleFormChangeForShell();
    }
  }

  async #initForm(): Promise<void> {
    this.enableAddressForm = this.origin === 'schedule' || this.origin === 'shell';

    const storedProfile = JSON.parse(
      sessionStorage.getItem('userProfile') as string
    );

    if (!storedProfile) return;

    this.userProfile = storedProfile as IUserProfileResponse;

    this.latLngLiteral = JSON.parse(localStorage.getItem('@Ford-coordinate') as string) || undefined;
    this.originalCoordinates = this.latLngLiteral;

    const address1 = this.userProfile.address?.addressLine1;
    const address2 = this.userProfile.address?.addressLine2;
    const address3 = this.userProfile.address?.addressLine3;
    const city = this.userProfile.address?.city;
    const country = this.userProfile.country?.countryCode;
    const zip = this.userProfile.address?.postalCode;
    const houseNumber = address2 ? ` ${address2},` : '';
    const addressUserProfile = `${address1},${houseNumber} ${city}, ${zip}, ${country}`;

    if (!address1 || !city || !country || !zip) {
      this.#handleEnableUpdateUserButton();
      this.isValidFields = true;
      return;
    }

    this.updateAddress.get('control')?.setValue(addressUserProfile);
    this.updateAddress.get('address3')?.setValue(address3);

    this.originAddress = addressUserProfile;
    this.originalAddress3 = address3 ?? '';

    this.noStreetNumber = !address2;    

    this.#handleShowIconsForm();
  }

  #searchEngine(): void {
    this.suggestObservable$ = this.updateAddress
      .get('control')
      ?.valueChanges.pipe(
        debounceTime(300),
        filter((text) => text?.length > 3),
        switchMap((text) => {
          if (
            (this.updateAddress.get('control')?.value !== this.originAddress ||
              this.updateAddress.get('address3')?.value !== this.originalAddress3) && this.enableSearch
          ) {
            this.#handleShowFinder();
            const regionCode = getCountryISOAlfa2(sessionStorage.getItem('country') as string);
            this.isValidFields = false;
            return this.googleMapsApiService.autocompletePlaces(text, regionCode, this.translate.currentLang);
          }
          return [];
        })
      ) as Observable<IAutocompleteResponse>;
  }

  async onSelectedAddressMap(addressFoundV2: IGeocodeResultV2): Promise<void> {
    if (!addressFoundV2.address_components) {
      console.warn('Address components not found');
      return;
    }

    // O geocodeByV2 (usado ao clicar no mapa) ja retorna o endereco estruturado "address",
    // da mesma forma que o placeDetailsV2 no fluxo de autocomplete. Usamos essa informacao
    // diretamente em #createUserPayload, evitando o parse manual de address_components
    // (fonte dos bugs de state/stateCode incorretos para alguns paises).
    this.addressDetailsV2 = addressFoundV2.address ?? null;

    const addressFound: IGeocodeResult = {
      formatted_address: addressFoundV2.formatted_address,
      geometry: addressFoundV2.geometry,
      address_components: addressFoundV2.address_components,
      place_id: addressFoundV2.place_id
    };

    this.addressFound = addressFound;
    this.#handleShowIconsForm();
    this.#handleEnableUpdateUserButton(false);
    this.latLngLiteral = addressFound.geometry.location;
    localStorage.setItem('@Ford-coordinate', JSON.stringify(this.latLngLiteral));
    this.updateAddress
      .get('control')
      ?.setValue(addressFound.formatted_address);
    this.emitUserData(this.checkIfOriginIsFromShell());

    if (this.origin === 'shell' && this.enableAddressForm) {
      await this.#handleFormChangeForShell();
    }
  }

  async getSuggest(place: IPlacePrediction): Promise<void> {
    this.#handleShowFinder(false);
    this.#handleShowIconsForm();

    this.enableSearch = false;
    this.updateAddress.get('address3')?.setValue('');

    // O placeDetailsV2 já retorna tudo que precisamos (lat/lng, formatted_address, place_id,
    // address_components e o endereço estruturado "address" tratado pelo backend), eliminando
    // a necessidade de chamar o endpoint V1 em paralelo.
    const placeDetailsV2 = await firstValueFrom(this.googleMapsApiService.placeDetailsV2(place.placeId));

    if (!placeDetailsV2) {
      this.#showGenericNotification(
        'update-delivery-address.api.address-not-found',
        'Error'
      );
      this.#handleLoading(false);
      this.enableSearch = true;
      return;
    }

    // O placeDetailsV2 já retorna o endereço estruturado (address) tratado pelo backend,
    // mais confiável para países com formatação inconsistente do Google (ex.: Colômbia).
    // Guardamos para usar na montagem do payload do usuário em #createUserPayload.
    this.addressDetailsV2 = placeDetailsV2.address ?? null;

    const addressFound: IGeocodeResult = {
      formatted_address: placeDetailsV2.formatted_address,
      geometry: {
        location: {
          lat: placeDetailsV2.geometry.location.lat,
          lng: placeDetailsV2.geometry.location.lng
        }
      },
      address_components: placeDetailsV2.address_components ?? [],
      place_id: placeDetailsV2.place_id
    };

    this.latLngLiteral = addressFound.geometry.location;
    localStorage.setItem('@Ford-coordinate', JSON.stringify(this.latLngLiteral));

    this.addressFound = addressFound;
    this.updateAddress.get('control')?.setValue(addressFound.formatted_address);
    this.#handleEnableUpdateUserButton(false);

    this.enableSearch = true;
    this.emitUserData(this.checkIfOriginIsFromShell());

    if (this.origin === 'shell' && this.enableAddressForm) {
      await this.#handleFormChangeForShell();
    }
  }

  #handleShowFinder(change = true): void {
    this.showFinder = change;
  }

  #handleShowIconsForm(change = true): void {
    this.showIconsForm = change;
  }

  handleShowForm(change = true): void {
    this.showForm = change;
    sessionStorage.setItem('showMapsScreen', String(!change));
    this.changeAddressOnMaps.emit(!this.showForm);
    if (!this.showForm && this.origin !== 'shell') {
      scrollToTop();
    }
  }

  #handleLoading(change = true): void {
    this.loading = change;

    if (!this.loading) {
      if (this.origin !== 'shell') scrollToTop();
    }
  }

  #handleEnableUpdateUserButton(change = true): void {
    this.enableUpdateUserButton = change;
  }

  toggleNoStreetNumber(event: Event): void {
    this.noStreetNumber = (event.target as HTMLInputElement).checked;
    
    this.#handleShowFinder(false);
    if (this.origin === 'shell') this.#handleFormChangeForShell();
  }

  blurAddressInput() {
    setTimeout(() => {
      this.#handleShowFinder(false);
    }, 100);
  }

  clearSearch(): void {
    this.#handleEnableUpdateUserButton();
    this.#handleShowFinder(false);
    // this.suggestSelected = null;
    this.addressFound = null;
    this.addressDetailsV2 = null;
    this.noStreetNumber = false;
    this.updateAddress.get('control')?.setValue('');
    if (this.origin === 'shell') this.#handleFormChangeForShell();
  }

  async onSubmit() {
    this.#handleLoading();
    const isUserPayloadCreated = await this.#createUserPayload();
    if (!isUserPayloadCreated) {
      this.#handleLoading(false);
      return;
    }
    await this.#updateUser();
    await this.#getLatLngFromAddress();
    this.#handleLoading(false);
    this.backPUD();
  }

  async #updateUser() {
    try {
      const storedProfile: IUserProfileResponse = JSON.parse(
        sessionStorage.getItem(StorageKeys.USER_PROFILE) ?? '{}'
      );

      const updateRequest: IUserProfileResponse = {
        ...storedProfile,
        address: {
          addressLine1: this.userProfile.address?.addressLine1 ?? '',
          addressLine2: this.userProfile.address?.addressLine2 ?? '',
          addressLine3: this.userProfile.address?.addressLine3 ?? '',
          addressLine4: this.userProfile.address?.addressLine4 ?? '',
          city: this.userProfile.address?.city ?? '',
          state: this.userProfile.address?.state ?? '',
          postalCode: this.userProfile.address?.postalCode ?? '',
        }
      };

      await this.coreUserService.saveUserProfile(updateRequest, true);

      this.#updateStorage();
      this.emitUserData(true);
      this.#showGenericNotification(
        'update-delivery-address.api.update-userprofile-success',
        'Success'
      );
    } catch (error) {
      this.#showGenericNotification(
        'update-delivery-address.api.update-userprofile-error',
        'Error',
        error
      );
      this.#handleLoading(false);
    }
  }

  #updateStorage(): void {
    localStorage.setItem(
      '@Ford-coordinate',
      JSON.stringify(this.latLngLiteral)
    );
    this.originalCoordinates = this.latLngLiteral;
    sessionStorage.setItem('userProfile', JSON.stringify(this.userProfile));
    localStorage.setItem('@Ford-userProfile', JSON.stringify(this.userProfile));
  }

  async #createUserPayload(): Promise<boolean> {
    if (!this.addressFound || !this.addressDetailsV2) {
      console.warn('Address details not found');
      return false;
    }

    const storedProfile: IUserProfileResponse = JSON.parse(
      sessionStorage.getItem(StorageKeys.USER_PROFILE) ?? '{}'
    );

    const complement = this.updateAddress.get('address3')?.value || '';

    // O objeto "address" (addressDetailsV2) já vem estruturado e validado pelo backend
    // (placeDetailsV2 / geocodeByV2), que agora também aplica a mesma regra de fallback
    // que antes existia aqui no front. Se um campo não vier preenchido no "address", é
    // porque o backend também não conseguiu extraí-lo dos address_components — não há mais
    // necessidade de reprocessar manualmente os address_components "crus" do Google.
    const addressLine1 = this.addressDetailsV2.addressLine1 || '';
    const addressLine2 = this.addressDetailsV2.addressLine2 || '';
    const addressLine4 = this.addressDetailsV2.addressLine4 || '';
    const city = this.addressDetailsV2.city || '';
    const state = this.addressDetailsV2.state || '';
    const stateCode = this.addressDetailsV2.stateCode || '';
    const postalCode = this.addressDetailsV2.postalCode || '';

    const userProfileToUpdate: IUserProfileResponse = {
      ...storedProfile,
      address: {
        addressLine1,
        addressLine2,
        addressLine3: complement,
        addressLine4,
        city,
        state: this.#getStateForCountry(stateCode, postalCode, storedProfile.country.countryCode) || state,
        postalCode,
      }
    };

    // Validação de negócio (mantida igual à versão anterior): a concessionária pode precisar
    // ir até a casa do cliente (atendimento domiciliar ou guincho), então rua/avenida, cidade,
    // estado e CEP são obrigatórios. O número da casa só é dispensado se o cliente marcar
    // explicitamente o checkbox "sem número" (noStreetNumber).
    const hasLocation = !!addressLine1;

    if (!state || !postalCode || !hasLocation || (!this.noStreetNumber && !addressLine2)) {
      this.#showGenericNotification(
        this.noStreetNumber
          ? 'update-delivery-address.api.update-userprofile-error.complete-message-without-number'
          : 'update-delivery-address.api.update-userprofile-error.complete-message',
        'Error'
      );

      this.#handleLoading(false);
      return false;
    }

    this.userProfile = {
      ...this.userProfile,
      names: userProfileToUpdate.names,
      address: userProfileToUpdate.address,
      emails: userProfileToUpdate.emails,
      phoneNumbers: userProfileToUpdate.phoneNumbers,
    };
    return true;
  }


  #getStateForCountry(stateCode: string, postalCode: string, country: string): string {
    if (country === COUNTRY_CODE.ARGENTINA && postalCode) {
      return postalCode.charAt(0);
    }
    return stateCode;
  }

  backPUD(change = true) {
    this.restartPudComponent.emit(change);
  }

  backToRemoteExperience() {
    if (this.originalCoordinates) {
      localStorage.setItem('@Ford-coordinate', JSON.stringify(this.originalCoordinates));
    } else {
      localStorage.removeItem('@Ford-coordinate');
    }
    this.backPUD();
  }

  isValidAndTouched(field: string): boolean {
    if (!this.updateAddress.controls[field]) return false;

    return (
      this.updateAddress.controls[field].dirty &&
      this.updateAddress.controls[field]?.touched &&
      this.updateAddress.controls[field]?.invalid
    );
  }

  containInputValue(field: string): boolean {
    return this.updateAddress.get(field)?.value !== '';
  }

  async #getLatLngFromAddress() {
    if (!this.addressFound) {
      this.#showGenericNotification(
        'update-delivery-address.api.lat-long-error',
        'Can not get customer lat and long'
      );
      this.#handleLoading(false);
      return;
    }

    const { geometry } = this.addressFound;

    if (geometry && this.dealer) {
      const distance = await firstValueFrom(this.googleMapsApiService.getDistanceBetweenPoints(
        geometry.location,
        {
          lat: this.dealer.latitude,
          lng: this.dealer.longitude,
        }
      ));
      this.dealer.distance = distance;
      localStorage.setItem('@Ford-coordinate', JSON.stringify(geometry.location));

    }
  }

  #showGenericNotification(
    translationKey: string,
    type: string,
    error?: unknown
  ) {
    if (error) console.error('Error: ', error);
    this.notificationService.add({
      text: this.translate.instant(translationKey),
      severity: type,
      timeout: 5000,
    });
  }

  #setupFormChangeTracking(): void {
    if (this.origin !== 'shell') {
      return;
    }

    this.updateAddress.get('control')?.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      filter(value => {
        return !!value && !!this.addressFound && this.enableAddressForm;
      })
    ).subscribe(() => {
      this.#handleFormChangeForShell();
    });

    this.updateAddress.get('address3')?.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged(),
      filter(() => {
        return !!this.addressFound && this.enableAddressForm;
      })
    ).subscribe(() => {
      this.#handleFormChangeForShell();
    });
  }

  async #handleFormChangeForShell(): Promise<void> {
    if (this.origin !== 'shell') {
      return;
    }

    try {
      const complement = this.updateAddress.get('address3')?.value;
      const controlValue = this.updateAddress.get('control')?.value;

      // Cenário 1: Endereço escolhido pelo autocomplete
      if (this.addressFound && this.addressFound.address_components) {
        await this.#createUserPayload();
      // Cenario 2: Limpeza de address1 pelo X (clearSearch)
      } else if (!controlValue || controlValue.trim() === '') {
        if (this.userProfile) {
          this.userProfile = {
            ...this.userProfile,
            address: {
              addressLine1: '',
              addressLine2: '',
              addressLine3: complement || '',
              addressLine4: '',
              city: this.userProfile.address?.city ?? '',
              state: this.userProfile.address?.state ?? '',
              postalCode: this.userProfile.address?.postalCode ?? '',
            },
          };
        }
      } else {
        if (this.userProfile) {
          this.userProfile = {
            ...this.userProfile,
            address: {
              addressLine1: this.userProfile.address?.addressLine1 ?? '',
              addressLine2: this.userProfile.address?.addressLine2 ?? '',
              addressLine3: complement || '',
              addressLine4: this.userProfile.address?.addressLine4 ?? '',
              city: this.userProfile.address?.city ?? '',
              state: this.userProfile.address?.state ?? '',
              postalCode: this.userProfile.address?.postalCode ?? '',
            },
          };
        }
      }
      this.userData.emit(this.userProfile);
    } catch (error) {
      console.error('Error handling form change for shell:', error);
    }
  }
}

export type PROJECT_ORIGIN = {
  schedule: 'schedule';
  shell: 'shell';
};
